// Footage rotation: the correction for a camera that was mounted upside down
// or on its side, stored as `footageRotation` on the project document in
// degrees clockwise.
//
// It is a property of the FOOTAGE, not of an edit — it describes how the
// camera was bolted to the wall. That is why it lives on the project rather
// than in `settings` (where two edits of one project could disagree about
// which way up the building is) and why it is set in the Footage tab, before
// the user ever opens the editor.
//
// Nothing is re-encoded when this changes. The stored file keeps the camera's
// orientation; the preview surfaces turn in CSS and the render turns in
// FFmpeg. So flipping this is free, instant and reversible at any point —
// including after an edit has already been rendered.
//
// Pure, and separate from `footageRotation.js` (which writes it), for the same
// reason `ordering.js` is separate from `footageOrder.js`: presentational
// components need these helpers, and importing the persistence module would
// pull the whole Firebase SDK into every one of them.
//
// The mirror of this table lives in the render worker
// (`processing/video_editing.py: ROTATION_FILTERS`), which re-validates rather
// than trusting what it reads: firestore.rules lets an owner write any field
// on their own project, so the browser is not the authority here. Keep the two
// in lock-step — a value this file can produce but the worker rejects would
// turn the preview and not the output, which is worse than doing nothing.

export const ROTATIONS = [0, 90, 180, 270];

// The four angles by their decimal spelling. A lookup, not `Number()`, for the
// lock-step reason above: the two languages' built-in coercions loosen in
// OPPOSITE directions, so leaning on them guarantees the mismatch this file's
// header warns about. `Number("0x5A")`, `Number("9e1")` and `Number("90.0")`
// are all 90 where Python's `int()` raises on each, and `int(90.5)` truncates
// to 90 where `Number` keeps the fraction. Either way one side turns and the
// other does not.
//
// So the grammar is stated instead of inherited, and the worker states the
// same one (`video_editing.py: _ROTATION_BY_SPELLING`): a whole number equal
// to one of the four angles, or that angle's plain decimal spelling as a
// string, with surrounding ASCII whitespace allowed. Nothing else is an angle
// in either language.
const BY_SPELLING = new Map(ROTATIONS.map((angle) => [String(angle), angle]));

// The padding rule, stated for the same reason the grammar is: `trim()` and
// Python's `strip()` are BOTH wrong for this, in opposite directions. `trim()`
// strips the byte-order mark U+FEFF and `strip()` does not (Python does not
// consider it whitespace), so a stored "\uFEFF90" would turn the preview and
// not the render. Python's `strip()` strips the control characters U+001C-
// U+001F and U+0085 and `trim()` does not, so "\u008590" would turn the render
// and not the preview. The six ASCII whitespace characters below are the set
// the two languages agree on, so that is the set both sides allow.
const ASCII_PADDING = /^[ \t\n\r\f\v]+|[ \t\n\r\f\v]+$/g;

/**
 * Coerce any stored value to one of 0/90/180/270.
 *
 * Absent, malformed or out-of-set reads as 0 — unrotated is the safe default,
 * because it is what every project did before this field existed.
 */
export function normalizeRotation(value) {
  if (typeof value === 'number') {
    // `isInteger` drops 90.5, NaN and Infinity; the spelling lookup then drops
    // every other whole number (100, 1e21, -90). Going through `String` also
    // folds -0 onto the canonical 0.
    return Number.isInteger(value) ? BY_SPELLING.get(String(value)) ?? 0 : 0;
  }
  if (typeof value === 'string') {
    return BY_SPELLING.get(value.replace(ASCII_PADDING, '')) ?? 0;
  }
  // Booleans, arrays, objects, null, undefined, Timestamps. Note this branch
  // is what keeps `[90]` at 0: `Number([90])` and `String([90])` are both 90,
  // because a one-element array stringifies to its element, while Python's
  // `int([90])` raises. Only a type check agrees across both.
  return 0;
}

/**
 * The rotation `steps` quarter-turns clockwise from `current` (negative turns
 * anticlockwise), wrapped into the 0-359 range.
 */
export function rotateBy(current, steps) {
  const base = normalizeRotation(current);
  return (((base + steps * 90) % 360) + 360) % 360;
}

/** "Rotated 90° right" / "Rotated 180°" / null when there is nothing to say. */
export function rotationLabel(rotation) {
  switch (normalizeRotation(rotation)) {
    case 90: return 'Rotated 90° right';
    case 180: return 'Rotated 180°';
    case 270: return 'Rotated 90° left';
    default: return null;
  }
}

/** True when the rotation turns the frame on its side, swapping its axes. */
export function swapsAxes(rotation) {
  const value = normalizeRotation(rotation);
  return value === 90 || value === 270;
}
